import React, { useState, useEffect, useCallback } from 'react';
import { X, AlertCircle } from 'lucide-react';

// Legacy (pre-CAP) client-side class match, used only as a fallback for years
// that have no CAP class-eligibility configured.
const isPackageApplicableToClass = (applicableClasses, studentClass) => {
    if (!applicableClasses || applicableClasses.trim() === '') return true;
    const allowed = applicableClasses.split(',').map(c => c.trim().toUpperCase());
    const sc = (studentClass || '').trim().toUpperCase();
    return allowed.some(a => sc.includes(a));
};

const AfterschoolEnrollModal = ({ apiCall, selectedYear, students, packages, onClose, onSuccess, showError }) => {
    const [modalData, setModalData] = useState({
        startDate: new Date().toISOString().split('T')[0],
        billingType: 'monthly',
        siblingDiscount: '0',
        discountReason: '',
        timeSlot: ''
    });
    const [studentSearch, setStudentSearch] = useState('');
    const [filteredStudents, setFilteredStudents] = useState([]);
    const [loading, setLoading] = useState(false);
    const [existingEnrollments, setExistingEnrollments] = useState([]);
    const [validationWarning, setValidationWarning] = useState('');
    const [searchFocused, setSearchFocused] = useState(false);

    // CAP catalog for the selected student's class (server-filtered by
    // AfterschoolPackageClasses). null = not loaded yet; [] = none configured.
    const [capPackages, setCapPackages] = useState(null);
    const [capAddons, setCapAddons] = useState([]);
    const [selectedAddonIds, setSelectedAddonIds] = useState([]);

    const handleStudentSearch = (searchTerm) => {
        setStudentSearch(searchTerm);
        setValidationWarning('');
        if (searchTerm.length >= 2) {
            const s = searchTerm.toLowerCase();
            setFilteredStudents(students.filter(st =>
                `${st.FirstName} ${st.LastName}`.toLowerCase().includes(s) ||
                (st.FatherName || '').toLowerCase().includes(s) ||
                (st.FamilyID || '').toLowerCase().includes(s) ||
                (st.Class || '').toLowerCase().includes(s)
            ));
        } else {
            setFilteredStudents([]);
        }
    };

    const selectStudent = async (student) => {
        try {
            const params = new URLSearchParams({ academicYear: selectedYear, familyId: student.FamilyID });
            const enrollments = await apiCall(`/afterschool/enrollments?${params.toString()}`);
            setExistingEnrollments(enrollments.filter(e => e.StudentID === student.StudentID && e.Status === 'Active'));

            setModalData(prev => ({
                ...prev,
                studentId: student.StudentID,
                familyId: student.FamilyID,
                studentName: `${student.FirstName} ${student.LastName}`,
                fatherName: student.FatherName,
                studentClass: student.Class,
                section: student.Section,
                packageId: undefined
            }));
            setStudentSearch(`${student.FirstName} ${student.LastName} - ${student.FatherName} (${student.Class}${student.Section || ''})`);
            setFilteredStudents([]);
            setSearchFocused(false);
            setSelectedAddonIds([]);

            // Load the CAP catalog eligible for this student's class.
            try {
                const r = await apiCall(`/afterschool/packages/for-class?academicYear=${encodeURIComponent(selectedYear)}&className=${encodeURIComponent(student.Class || '')}`);
                setCapPackages(Array.isArray(r.packages) ? r.packages : []);
                setCapAddons(Array.isArray(r.addons) ? r.addons : []);
            } catch {
                setCapPackages([]); setCapAddons([]);
            }
        } catch (error) {
            showError('Error checking enrollments: ' + error.message);
        }
    };

    // CAP packages when configured for this class; else the legacy prop list.
    const usingCap = Array.isArray(capPackages) && capPackages.length > 0;
    const packageOptions = usingCap
        ? capPackages
        : packages.filter(pkg => isPackageApplicableToClass(pkg.ApplicableClasses, modalData.studentClass));

    // Group options by GroupName for the dropdown (CAP sub-packages).
    const grouped = {};
    for (const p of packageOptions) {
        const g = p.GroupName || 'Packages';
        (grouped[g] = grouped[g] || []).push(p);
    }

    const handlePackageChange = (packageId) => {
        const pkg = packageOptions.find(p => String(p.PackageID) === String(packageId));
        setModalData(prev => ({
            ...prev,
            packageId,
            billingType: pkg && pkg.DailyPrice ? prev.billingType : 'monthly',
            timeSlot: (pkg && pkg.TimeSlot) || ''
        }));
    };

    const toggleAddon = (addonId) => {
        setSelectedAddonIds(prev => prev.includes(addonId) ? prev.filter(x => x !== addonId) : [...prev, addonId]);
    };

    const validateEnrollment = useCallback(() => {
        if (!modalData.packageId || !modalData.billingType) { setValidationWarning(''); return true; }
        const hasActiveMonthly = existingEnrollments.some(e => e.BillingType === 'monthly' && e.Status === 'Active');
        if (modalData.billingType === 'monthly' && hasActiveMonthly) {
            setValidationWarning('⚠️ This student already has an active MONTHLY enrollment. Monthly packages auto-renew, so adding another monthly enrollment will charge the family twice every month. Cancel the existing monthly enrollment first, or use DAILY billing.');
            return false;
        }
        setValidationWarning('');
        return true;
    }, [modalData.packageId, modalData.billingType, existingEnrollments]);

    useEffect(() => {
        validateEnrollment();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [modalData.studentId, modalData.packageId, modalData.billingType]);

    const selectedPackage = packageOptions.find(p => String(p.PackageID) === String(modalData.packageId));
    const packageCost = selectedPackage
        ? (modalData.billingType === 'monthly' ? (selectedPackage.MonthlyPrice || 0) : (selectedPackage.DailyPrice || 0))
        : 0;
    // Add-ons are monthly; only apply on monthly billing.
    const addonList = capAddons.filter(a => selectedAddonIds.includes(a.AddonID));
    const addonCost = modalData.billingType === 'monthly' ? addonList.reduce((s, a) => s + Number(a.MonthlyPrice || 0), 0) : 0;
    const discountAmount = parseFloat(modalData.siblingDiscount || 0);
    const finalCost = Math.max(0, packageCost + addonCost - discountAmount);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validateEnrollment()) { showError('Please resolve the enrollment warning before continuing.'); return; }
        if (!modalData.studentId) { showError('Please select a student.'); return; }
        if (!modalData.packageId) { showError('Please select a package.'); return; }
        if (discountAmount > 0 && !modalData.discountReason) { showError('Please select a discount reason when applying a discount.'); return; }
        try {
            setLoading(true);
            await apiCall('/afterschool/enrollments', {
                method: 'POST',
                body: JSON.stringify({
                    studentId: modalData.studentId,
                    familyId: modalData.familyId,
                    academicYear: selectedYear,
                    packageId: parseInt(modalData.packageId),
                    billingType: modalData.billingType,
                    startDate: modalData.startDate,
                    siblingDiscount: discountAmount,
                    discountReason: modalData.discountReason || null,
                    timeSlot: modalData.timeSlot || null,
                    addonIds: modalData.billingType === 'monthly' ? selectedAddonIds : []
                })
            });
            onSuccess();
        } catch (error) {
            showError(error.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                <div className="p-6 border-b border-gray-200 flex justify-between items-center sticky top-0 bg-white z-10">
                    <h3 className="text-xl font-bold text-gray-800">CAP Enrollment <span className="text-sm font-normal text-gray-500">({selectedYear})</span></h3>
                    <button onClick={onClose} className="text-gray-500 hover:text-gray-700"><X size={24} /></button>
                </div>

                <form onSubmit={handleSubmit} className="p-6 space-y-4">
                    {/* Student Search */}
                    <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">Search Student *</label>
                        <div className="relative">
                            <input
                                type="text"
                                value={studentSearch}
                                onChange={(e) => handleStudentSearch(e.target.value)}
                                onFocus={() => setSearchFocused(true)}
                                onBlur={() => setTimeout(() => setSearchFocused(false), 200)}
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                placeholder="Type student name, father name, family ID, or class..."
                                autoComplete="off"
                                required
                            />
                            {searchFocused && filteredStudents.length > 0 && (
                                <div className="absolute mt-2 max-h-60 overflow-y-auto border border-gray-300 rounded-lg bg-white shadow-lg w-full z-30">
                                    {filteredStudents.map(student => (
                                        <button key={student.StudentID} type="button" onClick={() => selectStudent(student)}
                                            className="w-full text-left px-4 py-3 hover:bg-blue-50 border-b border-gray-100">
                                            <div className="flex justify-between items-start">
                                                <div>
                                                    <p className="font-semibold text-gray-800">{student.FirstName} {student.LastName}</p>
                                                    <p className="text-sm text-gray-600">Father: {student.FatherName} | Family: {student.FamilyID}</p>
                                                </div>
                                                <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs font-semibold">{student.Class}{student.Section || ''}</span>
                                            </div>
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Selected Student */}
                    {modalData.studentId && (
                        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                            <p className="text-sm font-semibold text-green-800">Selected Student:</p>
                            <p className="text-sm text-green-700">{modalData.studentName} - Father: {modalData.fatherName}</p>
                            <p className="text-sm text-green-700">Family: {modalData.familyId} | Class: {modalData.studentClass}{modalData.section || ''}</p>
                            {existingEnrollments.length > 0 && (
                                <div className="mt-3 pt-3 border-t border-green-300">
                                    <p className="text-xs font-semibold text-green-800 mb-2">Current active enrollments:</p>
                                    {existingEnrollments.map((e, idx) => (
                                        <div key={idx} className="text-xs bg-white rounded p-2 mb-1 border border-green-200">
                                            <span className="font-semibold">{e.PackageName}</span>
                                            <span className="mx-2">•</span>
                                            <span className={`px-2 py-1 rounded ${e.BillingType === 'monthly' ? 'bg-orange-100 text-orange-800' : 'bg-blue-100 text-blue-800'}`}>{e.BillingType}</span>
                                            <span className="mx-2">•</span>
                                            <span>Balance: ${(e.BalanceRemaining || 0).toFixed(2)}</span>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}

                    {validationWarning && (
                        <div className="bg-red-50 border-2 border-red-300 rounded-lg p-4 flex items-start gap-3">
                            <AlertCircle className="text-red-600 flex-shrink-0 mt-0.5" size={20} />
                            <p className="text-sm text-red-800 font-semibold">{validationWarning}</p>
                        </div>
                    )}

                    {/* Package (class-filtered, grouped) */}
                    <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">Package *</label>
                        <select
                            required
                            value={modalData.packageId || ''}
                            onChange={(e) => handlePackageChange(e.target.value)}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                            disabled={!modalData.studentId}
                        >
                            <option value="">Select Package</option>
                            {Object.entries(grouped).map(([g, pkgs]) => (
                                <optgroup key={g} label={g}>
                                    {pkgs.map(pkg => (
                                        <option key={pkg.PackageID} value={pkg.PackageID}>
                                            {pkg.PackageName} — ${pkg.MonthlyPrice}/mo{pkg.DailyPrice ? ` or $${pkg.DailyPrice}/day` : ''}
                                        </option>
                                    ))}
                                </optgroup>
                            ))}
                        </select>
                        {!modalData.studentId && <p className="text-xs text-gray-500 mt-1">Select a student first to see eligible packages.</p>}
                        {modalData.studentId && packageOptions.length === 0 && (
                            <p className="text-xs text-orange-600 mt-1">No packages configured for {modalData.studentClass} in {selectedYear}. Set eligibility in CAP Settings.</p>
                        )}
                    </div>

                    {/* Add-ons (CAP, monthly only) */}
                    {selectedPackage && modalData.billingType === 'monthly' && capAddons.length > 0 && (
                        <div className="bg-purple-50 border border-purple-200 rounded-lg p-3">
                            <p className="text-xs font-semibold text-purple-900 mb-2">Add-ons (optional):</p>
                            <div className="space-y-1">
                                {capAddons.map(a => (
                                    <label key={a.AddonID} className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                                        <input type="checkbox" checked={selectedAddonIds.includes(a.AddonID)} onChange={() => toggleAddon(a.AddonID)} />
                                        {a.AddonName} <span className="text-purple-700 font-semibold">+${Number(a.MonthlyPrice).toFixed(2)}/mo</span>
                                    </label>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Billing + Start Date */}
                    <div className="grid md:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-2">Billing Type *</label>
                            <select
                                required
                                value={modalData.billingType || 'monthly'}
                                onChange={(e) => setModalData({ ...modalData, billingType: e.target.value })}
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                disabled={!selectedPackage?.DailyPrice}
                            >
                                <option value="monthly">Monthly (Auto-Renew)</option>
                                {selectedPackage?.DailyPrice && <option value="daily">Daily (One-time)</option>}
                            </select>
                            <p className="text-xs text-gray-500 mt-1">{modalData.billingType === 'monthly' ? '⚠️ Auto-renews each month until cancelled' : 'One-time daily rate'}</p>
                        </div>
                        <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-2">Start Date *</label>
                            <input type="date" required value={modalData.startDate}
                                onChange={(e) => setModalData({ ...modalData, startDate: e.target.value })}
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500" />
                        </div>
                    </div>

                    {/* Manual discount (editable later) */}
                    <div className="grid md:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-2">Discount Amount ($)</label>
                            <input type="number" step="0.01" min="0" value={modalData.siblingDiscount || '0'}
                                onChange={(e) => setModalData({ ...modalData, siblingDiscount: e.target.value })}
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                placeholder="0.00" disabled={!selectedPackage} />
                        </div>
                        <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-2">
                                Discount Reason {discountAmount > 0 && <span className="text-red-500">*</span>}
                            </label>
                            <select value={modalData.discountReason || ''}
                                onChange={(e) => setModalData({ ...modalData, discountReason: e.target.value })}
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                disabled={discountAmount <= 0} required={discountAmount > 0}>
                                <option value="">Select reason...</option>
                                <option value="Sibling">Sibling Discount</option>
                                <option value="Employee">Employee Discount</option>
                                <option value="Financial Aid">Financial Aid</option>
                                <option value="Promotion">Promotional Discount</option>
                                <option value="Loyalty">Loyalty Discount</option>
                                <option value="Special">Special Circumstances</option>
                                <option value="Other">Other</option>
                            </select>
                        </div>
                    </div>

                    {/* Cost breakdown */}
                    {selectedPackage && (
                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                            <p className="text-sm font-semibold text-gray-800 mb-3">Cost Breakdown:</p>
                            <div className="space-y-2">
                                <div className="flex justify-between"><span className="text-gray-700">Package ({modalData.billingType}):</span><span className="font-semibold">${packageCost.toFixed(2)}</span></div>
                                {addonList.map(a => (
                                    <div key={a.AddonID} className="flex justify-between text-purple-700"><span>+ {a.AddonName}:</span><span className="font-semibold">${Number(a.MonthlyPrice).toFixed(2)}</span></div>
                                ))}
                                {discountAmount > 0 && (
                                    <div className="flex justify-between text-green-600"><span>Discount ({modalData.discountReason || 'N/A'}):</span><span className="font-semibold">-${discountAmount.toFixed(2)}</span></div>
                                )}
                                <div className="border-t border-blue-300 pt-2 mt-2 flex justify-between">
                                    <span className="font-bold text-gray-800 text-lg">Final Cost:</span>
                                    <span className="font-bold text-blue-600 text-xl">${finalCost.toFixed(2)}</span>
                                </div>
                            </div>
                        </div>
                    )}

                    <div className="flex gap-3 pt-4">
                        <button type="submit"
                            disabled={loading || !modalData.studentId || !modalData.packageId || (validationWarning && modalData.billingType === 'monthly')}
                            className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed">
                            {loading ? 'Enrolling...' : 'Enroll Student'}
                        </button>
                        <button type="button" onClick={onClose}
                            className="px-6 bg-gray-200 hover:bg-gray-300 text-gray-800 font-semibold py-3 rounded-lg">Cancel</button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default AfterschoolEnrollModal;
